/**
 * LC Library Review API
 *
 * 도서관 step 복습. 퀴즈는 격리 세션(quiz_session_progress.learning_attempt_id non-null)에
 * 응답을 기록하며 canonical 통계/진도에는 영향을 주지 않는다. 비-퀴즈는 read-only payload.
 */
import { apiClient, type ApiResponse } from "./client";
import type {
    LcBuildupDataResponse,
    LcQuizResultDescription,
    LcQuizDataResponse,
    LcVideoDataResponse,
} from "./lcLearning";

export type LcLibraryReviewMode = "full" | "wrong_only";

export interface LcLibraryStartRequest {
    enrollmentId: number;
    lessonId: number;
    configComponentId: string;
    componentType: string; // 'video' | 'quiz' | 'guide' | 'toon' | 'buildup' | 'interactive' | 'warmup'
    mode?: LcLibraryReviewMode;
}

export interface LcLibraryStartResponse {
    componentType: string;
    sessionId: number | null; // 퀴즈 세션만 non-null
    componentProgressId: number | null; // 퀴즈 세션만 non-null
    videoData: LcVideoDataResponse | null;
    quizData: LcQuizDataResponse | null;
    guideData: unknown | null;
    toonData: unknown | null;
    buildupData: LcBuildupDataResponse | null;
    toolData: unknown | null;
    warmupData: unknown | null;
    isReview: true;
}

export interface LcLibrarySubmitRequest {
    quizId: string; // Big Integer ID — string으로 정밀도 유지
    submittedResponse: Record<string, unknown>;
    varInstantiation?: Record<string, unknown>;
    scriptId?: string;
    timeSpentSec?: number;
}

export interface LcLibrarySubmitResponse {
    isCorrect: boolean | null;
    partialCredit: number | null;
    corrects: (boolean | null)[];
    correctAnswers: unknown[];
    description: LcQuizResultDescription | null;
}

export async function startLibraryReview(
    request: LcLibraryStartRequest,
): Promise<LcLibraryStartResponse> {
    const res = await apiClient.post<ApiResponse<LcLibraryStartResponse>>(
        "/lc/learning/library-sessions/start",
        request,
    );
    return res.data.data;
}

export async function submitLibraryResponse(
    sessionId: number,
    request: LcLibrarySubmitRequest,
): Promise<LcLibrarySubmitResponse> {
    const res = await apiClient.post<ApiResponse<LcLibrarySubmitResponse>>(
        `/lc/learning/library-sessions/${sessionId}/response`,
        request,
    );
    return res.data.data;
}

export async function completeLibrarySession(
    sessionId: number,
    passScore?: number,
): Promise<void> {
    await apiClient.post<ApiResponse<void>>(
        `/lc/learning/library-sessions/${sessionId}/complete`,
        { passScore },
    );
}

/**
 * 즉석(stateless) 퀴즈 채점 — DB write 없음. 세션이 없는 경로(영상 pause-and-go 퀴즈) 전용.
 * 격리 세션 기록(submitLibraryResponse)과 구분된다.
 */
export interface LcQuizGradeRequest {
    quizId: string; // Big Integer ID — string으로 정밀도 유지
    submittedResponse: Record<string, unknown>;
    varInstantiation?: Record<string, unknown>;
    scriptId?: string;
}

export interface LcQuizGradeResponse {
    isCorrect: boolean | null;
    partialCredit: number | null;
    corrects: (boolean | null)[];
    correctAnswers: unknown[];
    description: LcQuizResultDescription | null;
}

export async function gradeLibraryQuiz(
    request: LcQuizGradeRequest,
): Promise<LcQuizGradeResponse> {
    const res = await apiClient.post<ApiResponse<LcQuizGradeResponse>>(
        "/lc/learning/quiz-grade",
        request,
    );
    return res.data.data;
}
